import { camelCase } from 'lodash';
import { useEffect } from 'react';
import {
  createHttpLink,
  DefaultContext as Context,
  ApolloLink,
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import { relayStylePagination } from '@apollo/client/utilities';

import { useAuth } from './auth/auth-context';
import { FullPageLoader } from './components/loader';
import { UserInitiatedAbort, AbortError, isAbortError } from './lib/error/utils/abort';
import { TimeoutError } from './lib/error/utils/timeout';
import { generateUUID } from './lib/utils';
import { setupChatWidget } from './lib/utils/support';

const UNAUTHENTICATED_REQUESTS = ['SignUp'];

export const AuthorisedApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const { user, getToken, logOut, loading } = useAuth();

  useEffect(() => {
    if (user?.email !== null && user?.email !== undefined) {
      setupChatWidget({ email: user.email });
    }
  }, [user?.email]);

  if (loading) {
    return <FullPageLoader />;
  }

  const httpLink = createHttpLink({
    uri: '/graphql',
    headers: {
      'X-Supersimple-Client': `Supersimple-app ${APP_VERSION}`,
    },
    fetch: (uri, options) => {
      return fetch(uri, options).catch((err) => {
        if (err.name === 'AbortError') {
          throw new TimeoutError(`Request timed out`);
        } else if (err === UserInitiatedAbort) {
          throw new AbortError();
        }

        throw err;
      });
    },
  });

  const timeoutLink = setContext((_, previousContext) => {
    const fetchOptions: Context = {};

    if (previousContext.fetchOptions?.signal !== undefined) {
      fetchOptions.signal = previousContext.fetchOptions.signal;
    }

    return { fetchOptions };
  });

  // Log any GraphQL errors or network error that occurred
  const errorLink = onError(({ graphQLErrors, networkError }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        if (message.includes('Bad token')) {
          logOut();
        }
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
        );
      });
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError}`);

      if (isAbortError(networkError)) {
        throw new AbortError();
      }
    }
  });

  const authLink = setContext(async ({ operationName }) => {
    if (operationName !== undefined && UNAUTHENTICATED_REQUESTS.includes(operationName)) {
      return {};
    }

    const token = await getToken();

    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  });

  const traceLink = new ApolloLink((operation, forward) => {
    operation.setContext(({ headers = {} }) => ({
      headers: {
        ...headers,
        'X-Trace-ID': generateUUID(),
      },
    }));
    return forward(operation);
  });

  const client = new ApolloClient({
    defaultOptions: {
      watchQuery: {
        fetchPolicy: 'no-cache',
      },
    },
    cache: new InMemoryCache({
      typePolicies: {
        Account: {
          keyFields: ['accountId'],
          fields: {
            query: relayStylePagination([
              'baseModelId',
              'pipeline',
              'sort',
              'first',
              'variables',
              'timezone',
            ]),
            conversation: {
              read(_, { args, toReference }) {
                return toReference({
                  __typename: 'Conversation',
                  conversationId: args?.conversationId,
                });
              },
            },
          },
        },
        Model: { keyFields: ['modelId'], fields: { entities: relayStylePagination() } },
        EntityRelation: {
          fields: {
            entities: relayStylePagination(),
          },
        },
        Conversation: {
          keyFields: ['conversationId'],
          fields: {
            messages: {
              merge(existing, incoming, { mergeObjects }) {
                return incoming.map((message: { id: string }) =>
                  mergeObjects(
                    (existing ?? []).find((m: { id: string }) => m.id === message.id) ?? {},
                    message,
                  ),
                );
              },
            },
          },
        },
      },
      dataIdFromObject: (obj) => {
        const fieldName = `${camelCase(obj.__typename)}Id`;
        if (typeof obj[fieldName] === 'string') {
          return `${obj.__typename}:${obj[fieldName]}`;
        }
        return undefined;
      },
    }),
    link: ApolloLink.from([authLink, timeoutLink, errorLink, traceLink, httpLink]),
  });

  return <ApolloProvider client={client}> {children} </ApolloProvider>;
};
